import Modal from "components/commons/modal/Modal";
import { strings } from "intl/fa";
import React, { useEffect, useState, useMemo } from "react";
import {
  getMonthDays,
  getJalaliDateObject,
  jalaliToEpoch,
  OneDayInMS,
} from "utils/time";
import { useAppSelector } from "utils/store";
import { useRequest } from "hooks/useRequest";
import styles from "./CapacityDatePicker.module.scss";
import { GetPeriodCapacityDataThunk } from "core/periodCapacityData/periodCapacityData.thunk";
import MonthPagination from "./MonthPagination";
import DatePickerItems, { isDayDisabled } from "./DatePickerItems";
import { DismissibleHelp } from "components/commons/helper/DismissibleHelp";
import { useConfigs } from "hooks/useConfigs";
import { calcCtr, calcViewCountForReservation } from "utils/utils";
import { LARGE_ORDER_BREAKPOINT } from "utils/constants";

interface PropTypes {
  onSubmit?: (selectedDate: number) => void;
  onClose?: () => void;
  helpText?: string;
  previewMode?: boolean;
  wrapperStyle?: any;
  finalViewCount: number;
  disableCapacity?: boolean;
  showButton?: boolean;
  isSponsored: boolean;
}

function CapacityDatePicker(props: PropTypes) {
  const todaysJalaliDate = getJalaliDateObject();
  const [selectedDate, setSelectedDate] = useState<number>();
  const [showHelper, setShowHelper] = useState<boolean>(!!props.helpText);
  const [gotNextMonthData, setGotNextMonthData] = useState(false);

  const capacityData = useAppSelector(
    (state) => state.periodCapacityData.capacityData
  )?.data;

  const { sendRequest: getPeriodCapacityData, loading } = useRequest(
    GetPeriodCapacityDataThunk
  );

  const configs = useConfigs();
  const todaysEpoch = jalaliToEpoch(getJalaliDateObject());
  const validStart = todaysEpoch + configs.ad_start_delay * OneDayInMS;
  const { view_count, click_count } = useAppSelector(
    (state) => state.orderAd.info
  );
  const { selectedOrder } = useAppSelector((state) => state.ordersList);
  const viewNumber = Number(view_count || selectedOrder?.set_view);
  const clickNumber = Number(click_count || selectedOrder?.set_click);

  const daysStatus = useMemo(() => {
    return capacityData.map((cap) => {
      const finalViewCount = calcViewCountForReservation(
        Number(viewNumber) || 0,
        Number(clickNumber) || 0,
        configs.click_to_view_coef
      );
      const isDisabled = isDayDisabled({
        dateObj: new Date(cap.date),
        isLargeOrder: finalViewCount > LARGE_ORDER_BREAKPOINT,
        capData: cap,
        segments: configs.start_hours_capacity,
        isSponsored: props.isSponsored,
        validStart: validStart,
        finalViewCount: finalViewCount,
      });

      return isDisabled;
    });
  }, [
    capacityData,
    viewNumber,
    clickNumber,
    configs.click_to_view_coef,
    configs.start_hours_capacity,
    props.isSponsored,
    validStart,
  ]);

  const isCurrentMonthFull = useMemo(() => {
    return daysStatus.length && daysStatus.every((day) => day === true);
  }, []);

  useEffect(() => {
    // get month capacity Data
    const begin = jalaliToEpoch({
      ...todaysJalaliDate,
      day: 1,
      month: todaysJalaliDate.month,
    });
    const end = jalaliToEpoch({
      ...todaysJalaliDate,
      day: getMonthDays(todaysJalaliDate.year, todaysJalaliDate.month),
      month: todaysJalaliDate.month,
    });
    paginationChangeHandler(begin, end);
  }, []);

  const distanceFromSaturday = capacityData[0]
    ? new Date(capacityData[0]?.date).getDay() === 6
      ? 0
      : new Date(capacityData[0]?.date).getDay() + 1
    : 0;

  const submitDateHandler = () => {
    if (props.previewMode) {
      props.onClose?.();
    }
    if (!selectedDate) {
      return;
    }
    props.onSubmit?.(selectedDate);
    props.onClose?.();
  };

  const paginationChangeHandler = (
    monthBeginEpoch: number,
    monthEndEpoch: number
  ) => {
    getPeriodCapacityData({
      begin_date: new Date(monthBeginEpoch).toISOString(),
      end_date: new Date(monthEndEpoch + OneDayInMS).toISOString(),
    });

    setSelectedDate(undefined);
  };

  const beforeEmptyDivs = useMemo(
    () =>
      Array.from(Array(distanceFromSaturday)).map((_, index) => (
        <div key={`before-${index}`} style={{ width: "12%" }}></div>
      )),
    [distanceFromSaturday]
  );

  return (
    <div style={props.wrapperStyle} className={styles.ModalWrapper}>
      <Modal
        title={strings.ad_time_select.time_select_header}
        onClose={props.onClose}
        mobileBottomSheet={true}
        primaryButton={
          props.showButton
            ? {
                label: strings.common.button.submit,
                onClick: submitDateHandler,
                disable: props.previewMode ? false : !selectedDate,
              }
            : undefined
        }
      >
        <DismissibleHelp helpText={props.helpText!} />
        <MonthPagination
          allowedNext={1}
          onChange={(monthBeginEpoch, monthEndEpoch) =>
            paginationChangeHandler(monthBeginEpoch, monthEndEpoch)
          }
          openOnNextMonth={gotNextMonthData}
        />
        {loading || capacityData.length === 0 ? (
          <ShimmerComponent />
        ) : (
          <div className={styles.CalenderWrapper}>
            {/* Empty divs before so friday will always be the last column*/}
            {beforeEmptyDivs}
            {/* Rendering days: */}
            <DatePickerItems
              capacityData={capacityData}
              previewMode={props.previewMode}
              setSelectedDate={setSelectedDate}
              selectedDate={selectedDate}
              viewCount={props.finalViewCount}
              disableCapacity={props.disableCapacity}
              daysStatus={daysStatus}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

export default CapacityDatePicker;

export function ShimmerComponent() {
  const RowShimmer = (
    <div className={styles.AdDateLoadingShimmer}>
      <div className={styles.ShimmerLine}></div>
      <div className={styles.Shimmer}></div>
    </div>
  );
  return (
    <>
      {RowShimmer}
      {RowShimmer}
      {RowShimmer}
      {RowShimmer}
      {RowShimmer}
      {RowShimmer}
    </>
  );
}
